import { useInspection } from "./InspectionContext";
import type { LocalPhoto } from "./InspectionContext";
import { Breadcrumb, CheckIcon, formatBytes } from "./shared";

export function ProcessingScreen() {
  const { photos } = useInspection();
  const done = photos.filter((p) => p.status === "done").length;
  const total = photos.length;

  const summedProgress = photos.reduce((sum, p) => {
    if (p.status === "done") return sum + 100;
    if (p.status !== "uploading") return sum;
    const inner = p.progress ?? 0;
    if (p.phase === "uploading") return sum + Math.round(inner * 0.3);
    if (p.phase === "analyzing") return sum + 30 + Math.round(inner * 0.65);
    return sum;
  }, 0);
  const pct = total === 0 ? 0 : Math.round(summedProgress / total);

  return (
    <>
      <Breadcrumb trail={["Inspections", "New"]} />
      <div className="mt-3">
        <h1 className="text-3xl font-bold tracking-tight">
          Analysing vehicle photos
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          Running damage detection. This usually takes a few seconds per
          photo — please keep this tab open.
        </p>
      </div>

      <div className="mt-8 rounded-card border border-border-grey bg-white p-6">
        <div className="flex items-baseline justify-between">
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-black leading-none">
              {done}
              <span className="text-text-secondary">/{total}</span>
            </span>
            <span className="text-sm font-semibold uppercase tracking-wider text-text-secondary">
              Photos analysed
            </span>
          </div>
          <span className="text-sm font-bold">{pct}%</span>
        </div>
        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-surface-light">
          <div
            className="h-full bg-accent transition-[width] duration-500 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="mt-6 rounded-card border border-border-grey bg-white">
        <div className="border-b border-border-grey px-5 py-3">
          <h3 className="text-sm font-bold uppercase tracking-wider">
            Queue
          </h3>
        </div>
        <ul className="divide-y divide-border-grey">
          {photos.map((p) => (
            <QueueRow key={p.id} photo={p} />
          ))}
        </ul>
      </div>
    </>
  );
}

function QueueRow({ photo }: { photo: LocalPhoto }) {
  return (
    <li className="flex items-center gap-4 px-5 py-3">
      <div className="h-12 w-16 shrink-0 overflow-hidden rounded-md bg-hero-charcoal">
        <img
          src={photo.previewUrl}
          alt={photo.file.name}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">
          {photo.file.name}
        </div>
        <div className="truncate text-xs text-text-secondary">
          {formatBytes(photo.file.size)}
        </div>
      </div>
      <StatusIndicator photo={photo} />
    </li>
  );
}

function StatusIndicator({ photo }: { photo: LocalPhoto }) {
  if (photo.status === "done") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-sixt-black">
        <span className="grid h-5 w-5 place-items-center rounded-full bg-sixt-black text-white">
          <CheckIcon />
        </span>
        Analysed
      </span>
    );
  }
  if (photo.status === "uploading") {
    const pct = photo.progress ?? 0;
    const label = photo.phase === "uploading" ? "Uploading" : "Analysing";
    return (
      <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent">
        <Spinner />
        <span>{label}</span>
        <span className="tabular-nums">{pct}%</span>
      </span>
    );
  }
  if (photo.status === "error") {
    return (
      <span className="text-xs font-semibold uppercase tracking-wider text-[#D32F2F]">
        Failed
      </span>
    );
  }
  return (
    <span className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
      Queued
    </span>
  );
}

function Spinner() {
  return (
    <span className="relative inline-block h-4 w-4">
      <span className="absolute inset-0 rounded-full border-2 border-accent/20" />
      <span className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-accent" />
    </span>
  );
}
